import React from "react";
import { useTranslation } from "react-i18next";
import i18next from "src/i18n/config";
import { getAppVersion } from "src/services/configService";
import { LocalStorage } from "src/services/localStorageService";
import LanguageSwitcher from "src/ui/shared/LanguageSwitcher/LanguageSwitcher";
import { makeStyles } from "tss-react/mui";

import { ConfirmDialog } from "@dashboard/devices/components/shared/ConfirmDialog";
import { PageContainer } from "@dashboard/shared/components/PageContainer";
import { Box, Theme } from "@mui/material";
import { withMetaDescription } from "@navigation/utils/withMetaDescription";
import AuthService from "@services/authService";
import { DangerOutlinedButton } from "@shared/CustomButton";
import { UncontrolledTextField } from "@shared/form/TextField";
import { SectionPanel } from "@shared/SectionPanel/SectionPanel";
import { showSuccessToast } from "@shared/toasts/Toasts";

import { TenantSwitcher } from "./TenantSwitcher";
import { ThemeSwitcher } from "./ThemeSwitcher";

const useStyles = makeStyles()((theme: Theme) => ({
    switchLabel: {
        marginBottom: theme.spacing(1),
        fontWeight: 600,
    },
    switchContent: { display: "flex", flexDirection: "column" },
    settingsRows: {
        display: "flex",
        flexDirection: "column",
        gap: theme.spacing(3),
    },
    settingsRow: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        width: "100%",
        gap: theme.spacing(2),
    },
    controlBox: {
        width: 200,
    },
    confirmDialogParagraph: {
        marginBottom: theme.spacing(2),
    },
    confirmDialogListTitle: {
        fontWeight: 600,
        marginBottom: theme.spacing(1),
    },
    confirmDialogList: {
        margin: 0,
        paddingLeft: theme.spacing(3),
    },
}));

function AccountPortalSettingsPage() {
    const [isResetConfirmOpen, setIsResetConfirmOpen] = React.useState(false);

    const appVersion = getAppVersion();
    const { t } = useTranslation(["common", "pages"]);
    const { classes } = useStyles();

    const handleResetSettings = () => {
        LocalStorage.reset();
        showSuccessToast("Portal settings have been reset to default");
        window.location.reload();
    };
    const handleResetConfirm = () => {
        handleResetSettings();
    };

    return (
        <PageContainer fullWidth={false}>
            <SectionPanel
                title={t("pages:settingsPage.portalSettings.title")}
                actions={
                    <DangerOutlinedButton
                        size="small"
                        startIcon={<i className="fa-solid fa-fw fa-arrow-rotate-left" />}
                        onClick={() => setIsResetConfirmOpen(true)}
                        name="reset-settings"
                    >
                        {t("pages:settingsPage.portalSettings.resetSettings.button")}
                    </DangerOutlinedButton>
                }
            >
                <Box className={classes.settingsRows}>
                    <Box className={classes.settingsRow}>
                        <Box className={classes.switchContent}>
                            <span className={classes.switchLabel}>{t("pages:settingsPage.language.label")}</span>
                            <div>{t("pages:settingsPage.language.description")}</div>
                        </Box>
                        <Box className={classes.controlBox}>
                            <LanguageSwitcher showLabel={false} fullWidth={true} />
                        </Box>
                    </Box>

                    <Box className={classes.settingsRow}>
                        <Box className={classes.switchContent}>
                            <span className={classes.switchLabel}>
                                {t("pages:settingsPage.portalSettings.themeSwitch.label")}
                            </span>
                            <div>{t("pages:settingsPage.portalSettings.themeSwitch.description")}</div>
                        </Box>
                        <Box className={classes.controlBox}>
                            <ThemeSwitcher />
                        </Box>
                    </Box>

                    {AuthService.mayUserSeeTenantSwitcher() ? (
                        <Box className={classes.settingsRow}>
                            <Box className={classes.switchContent}>
                                <span className={classes.switchLabel}>
                                    {t("pages:settingsPage.portalSettings.tenantSwitch.label")}
                                </span>
                                <div>{t("pages:settingsPage.portalSettings.tenantSwitch.description")}</div>
                            </Box>
                            <Box className={classes.controlBox}>
                                <TenantSwitcher />
                            </Box>
                        </Box>
                    ) : null}

                    <Box className={classes.settingsRow}>
                        <Box className={classes.switchContent}>
                            <span className={classes.switchLabel}>{t("pages:settingsPage.portalSettings.version.label")}</span>
                            <div>{t("pages:settingsPage.portalSettings.version.description")}</div>
                        </Box>
                        <UncontrolledTextField
                            className={classes.controlBox}
                            value={appVersion ?? t("common:status.unknown")}
                            disabled
                            slotProps={{
                                htmlInput: { "aria-label": t("pages:settingsPage.portalSettings.version.label") },
                            }}
                        />
                    </Box>
                </Box>
            </SectionPanel>

            <ConfirmDialog
                open={isResetConfirmOpen}
                onClose={() => setIsResetConfirmOpen(false)}
                onConfirm={handleResetConfirm}
                title={t("pages:settingsPage.portalSettings.resetSettings.confirmModal.title")}
                primaryActionText={t("pages:settingsPage.portalSettings.resetSettings.confirmModal.confirmButton")}
                dangerButton
            >
                <Box component="p" className={classes.confirmDialogParagraph}>
                    {t("pages:settingsPage.portalSettings.resetSettings.confirmModal.description")}
                </Box>
                <Box component="p" className={classes.confirmDialogListTitle}>
                    {t("pages:settingsPage.portalSettings.resetSettings.confirmModal.whatWillBeReset")}
                </Box>
                <Box component="ul" className={classes.confirmDialogList}>
                    <li>
                        {t("pages:settingsPage.portalSettings.resetSettings.confirmModal.whatWillBeResetItems.languageAndTheme")}
                    </li>
                    <li>
                        {t("pages:settingsPage.portalSettings.resetSettings.confirmModal.whatWillBeResetItems.uiPreferences")}
                    </li>
                    <li>
                        {t("pages:settingsPage.portalSettings.resetSettings.confirmModal.whatWillBeResetItems.tableSettings")}
                    </li>
                    <li>
                        {t("pages:settingsPage.portalSettings.resetSettings.confirmModal.whatWillBeResetItems.collapsedSections")}
                    </li>
                </Box>
            </ConfirmDialog>
        </PageContainer>
    );
}

export default withMetaDescription(AccountPortalSettingsPage, {
    title: i18next.t("pages:settingsPage.pages.portal.metaTitle"),
    description: i18next.t("pages:settingsPage.pages.portal.metaDescription"),
});
